// auth
import { AuthGuard } from 'src/auth/guard';
// components
import { Suspense, lazy } from 'react';
import { Outlet } from 'react-router-dom';
import DocsLayout from 'src/layouts/docs/layout';
import { LoadingScreen } from 'src/components/loading-screen';

// ----------------------------------------------------------------------

const Overview = lazy(() => import('src/pages/docs/overview'));
// User
const UserRoles = lazy(() => import('src/pages/docs/user/roles'));
const Register = lazy(() => import('src/pages/docs/user/register'));
const InviteUser = lazy(() => import('src/pages/docs/user/invite'));
const PasswordReset = lazy(() => import('src/pages/docs/user/passwordReset'));
const TwoFactorAuthentication = lazy(() => import('src/pages/docs/user/two-factor-authentication'));
// Setup
const SetupApproval = lazy(() => import('src/pages/docs/setup/approval'));
const OrgCredentials = lazy(() => import('src/pages/docs/setup/orgCredentials'));
const TranslationEngine = lazy(() => import('src/pages/docs/setup/translationEngine'));
const Glossary = lazy(() => import('src/pages/docs/setup/glossary'));
const Notifications = lazy(() => import('src/pages/docs/setup/notifications'));
// Meta Translator
const RetrieveMetadata = lazy(() => import('src/pages/docs/translationWorkbench/retrieveMetadata'));
const FilterMetadata = lazy(() => import('src/pages/docs/translationWorkbench/filterMetadata'));
const TranslateMetadata = lazy(
  () => import('src/pages/docs/translationWorkbench/translateMetadata')
);
const DeployMetadata = lazy(() => import('src/pages/docs/translationWorkbench/deployMetadata'));
const Limit = lazy(() => import('src/pages/docs/translationWorkbench/limit'));
const TokenHistory = lazy(() => import('src/pages/docs/translationWorkbench/tokenHistory'));
// MTJob
const CreateUpdateJob = lazy(() => import('src/pages/docs/MTJob/createUpdateJob'));
const MTJobStatus = lazy(() => import('src/pages/docs/MTJob/status'));
const CompareOrgs = lazy(() => import('src/pages/docs/MTJob/compareOrgs'));
const OrgsDeployment = lazy(() => import('src/pages/docs/MTJob/orgsDeployment'));
// Salesforce
const Translate = lazy(
  () => import('src/pages/docs/salesforce/translate')
);
const DetectLanguage = lazy(() => import('src/pages/docs/salesforce/detectLanguage'));
const AuthenticationSalesforce = lazy(
  () => import('src/pages/docs/salesforce/authenticationSalesforce')
);
const InstallPackage = lazy(() => import('src/pages/docs/salesforce/installPackage'));
const EditRecordPage = lazy(() => import('src/pages/docs/salesforce/editRecordPage'));
const EmailAI = lazy(() => import('src/pages/docs/salesforce/emailAI'));
const CaseCommentAI = lazy(() => import('src/pages/docs/salesforce/caseCommentAI'));
const N8nSetup = lazy(() => import('src/pages/docs/salesforce/n8nSetup'));
const RecordAI = lazy(() => import('src/pages/docs/salesforce/recordAI'));
const AIService = lazy(() => import('src/pages/docs/salesforce/aiService'));
const FileAI = lazy(() => import('src/pages/docs/salesforce/fileAI'));
const FlowSTConfiguration = lazy(() => import('src/pages/docs/salesforce/flowSTConfiguration'));
const STCiCdComponents = lazy(() => import('src/pages/docs/salesforce/STCiCdComponents'));
const AIAsyncResponseCheck = lazy(() => import('src/pages/docs/salesforce/aiAsyncResponseCheck'));
const JsonParser = lazy(() => import('src/pages/docs/salesforce/jsonParser'));
const Feedback = lazy(() => import('src/pages/docs/salesforce/feedback'));
const Chatbot = lazy(() => import('src/pages/docs/salesforce/chatbot'));


// SimpleT AI
const PromptBuilderGuide = lazy(() => import('src/pages/docs/promptBuilder/guide'));
const AICommandCenterGuide = lazy(() => import('src/pages/docs/simpleTAI/aiCommandCenterGuide'));
const LLMManagerGuide = lazy(() => import('src/pages/docs/llm-manager-guide'));

// OpenAPIs
const Token = lazy(() => import('src/pages/docs/openApis/token'));

// Billing
const BillingGuide = lazy(() => import('src/pages/docs/billing/guide'));

// ----------------------------------------------------------------------
const queryParameters = new URLSearchParams(window.location.search);

const inviteKey = queryParameters.get('inviteKey');

export const docsRoutes = [
  {
    path: 'docs',
    element: (
      // <AuthGuard inviteKey={inviteKey}>
      // <DashBoardProvider>
      //   <RealTimeProvider>
      <DocsLayout>
        <Suspense fallback={<LoadingScreen />}>
          <Outlet />
        </Suspense>
      </DocsLayout>
      //   </RealTimeProvider>
      // </DashBoardProvider>
      // </AuthGuard>
    ),
    children: [
      { path: 'overview', element: <Overview /> },
      { path: 'user/roles', element: <UserRoles /> },
      { path: 'user/twoFactorAuthentication', element: <TwoFactorAuthentication /> },
      { path: 'user/register', element: <Register /> },
      { path: 'user/invite', element: <InviteUser /> },
      { path: 'user/passwordReset', element: <PasswordReset /> },
      { path: 'setup/approval', element: <SetupApproval /> },
      { path: 'setup/orgCredentials', element: <OrgCredentials /> },
      { path: 'setup/translationEngine', element: <TranslationEngine /> },
      { path: 'setup/glossary', element: <Glossary /> },
      { path: 'setup/notifications', element: <Notifications /> },
      { path: 'translationWorkbench/retrieveMetadata', element: <RetrieveMetadata /> },
      { path: 'translationWorkbench/filterMetadata', element: <FilterMetadata /> },
      { path: 'translationWorkbench/deployMetadata', element: <DeployMetadata /> },
      { path: 'translationWorkbench/translateMetadata', element: <TranslateMetadata /> },
      { path: 'translationWorkbench/limit', element: <Limit /> },
      { path: 'translationWorkbench/tokenHistory', element: <TokenHistory /> },
      { path: 'MTJob/createUpdateJob', element: <CreateUpdateJob /> },
      { path: 'MTJob/status', element: <MTJobStatus /> },
      { path: 'MTJob/compareOrgs', element: <CompareOrgs /> },
      { path: 'MTJob/orgsDeployment', element: <OrgsDeployment /> },
      { path: 'salesforce/components/detectLanguage', element: <DetectLanguage /> },
      { path: 'salesforce/components/translate', element: <Translate /> },
      { path: 'salesforce/setup/authenticationSalesforce', element: <AuthenticationSalesforce /> },
      { path: 'salesforce/setup/installPackage', element: <InstallPackage /> },
      { path: 'salesforce/setup/editRecordPage', element: <EditRecordPage /> },
      { path: 'salesforce/setup/n8n-setup', element: <N8nSetup /> },
      { path: 'salesforce/components/emailAI', element: <EmailAI /> },
      { path: 'salesforce/components/caseCommentAI', element: <CaseCommentAI /> },
      { path: 'salesforce/components/recordAI', element: <RecordAI /> },
      { path: 'salesforce/components/fileAI', element: <FileAI /> },
      { path: 'salesforce/components/aiService', element: <AIService /> },
      { path: 'salesforce/setup/flowSTConfiguration', element: <FlowSTConfiguration /> },
      { path: 'salesforce/components/stCiCdComponent', element: <STCiCdComponents /> },
      { path: 'salesforce/components/aiAsyncResponseCheck', element: <AIAsyncResponseCheck /> },
      { path: 'salesforce/components/stJsonParser', element: <JsonParser /> },
      { path: 'salesforce/components/feedback', element: <Feedback /> },
      { path: 'salesforce/components/aiAgent', element: <Chatbot /> },
      { path: 'simpleTAI/promptBuilderGuide', element: <PromptBuilderGuide /> },
      { path: 'simpleTAI/aiCommandCenterGuide', element: <AICommandCenterGuide /> },
      { path: 'simpleTAI/llmManagerGuide', element: <LLMManagerGuide /> },
      { path: 'openAPIs/token', element: <Token /> },
      { path: 'billing/guide', element: <BillingGuide /> },
    ],
  },
];
